import { apiClient } from './client';
import type { LeaveTypeKey } from '../lib/leave-type';

/**
 * HR API client (slfg --from-doc init-docs/, scope A) — backend
 * `apps/chat-server/src/modules/hr/{leaves,business-trips,overtime}.routes.ts` 와 1:1.
 *
 * out-of-scope: leave 승인/반려 endpoint, early-leaves, 외부 시스템 연동.
 */

/**
 * 휴가 코드 — 목록은 `lib/leave-type.ts` 한 곳에서 나온다 (2026-09-16 · 사본 합치기).
 * ★ 종전에는 같은 여덟을 여기에도 손으로 적어 두어, 한쪽만 고치면 조용히 어긋났다.
 *   (반반차 2종은 2026-08-24 에 들어왔다 — 서버가 먼저 나가야 400 이 안 난다.)
 * ★ S6 의 합친 코드(`half` · `quarter`)도 **그 목록에서** 온다 — 여기에 따로 적지 않는다.
 */
export type LeaveType = LeaveTypeKey;
export type RequestStatus = 'PENDING' | 'APPROVED' | 'REJECTED';
export type TripType = 'business_trip' | 'outside_work';

export interface LeaveDto {
  id: string;
  type: string;
  startDate: string;
  endDate: string;
  days: number;
  /** 휴가 길이(분) — **진실값**. 옛 서버는 안 준다(2026-08-24 이전). */
  minutes?: number;
  /** `1일` · `4시간` — 서버가 만든 문구. 없으면 화면이 일수로 되돌아간다. */
  amountLabel?: string;
  /** 반차 · 반반차 구간 `HH:mm` (S6 · 2026-09-16). 옛 기록 · 종일은 null · 옛 서버는 키가 없다. */
  startTime?: string | null;
  endTime?: string | null;
  reason: string;
  status: string;
  approvedBy: string | null;
  approvedAt: string | null;
  createdAt: string;
}

export interface LeaveSummaryDto {
  /**
   * ★★★ **`remaining` 과 `available` 은 다른 값이다** (2026-09-01 · 하네스 판 F).
   *
   *    · `remaining` = 부여 − **사용** — 담당자 화면(「연차 관리」)이 쓰던 뜻
   *    · `available` = 잔여 − **신청중** — 직원 화면이 「잔여」로 크게 보여 주던 값
   *
   *    종전에는 **같은 이름이 두 뜻**이라 같은 사람의 잔여가 화면마다 달랐다. 이제 서버가
   *    둘을 갈라 준다 — 화면은 **`available` 을 보여야 종전과 같은 숫자**다.
   * ★ `total` 이 `null` 이면 「모른다」다(입사일도 부여 기록도 없다) — 0 도 15 도 아니다.
   * ★ `available` 은 옛 서버가 안 준다 — 그때는 `remaining` 이 그 뜻이었다(`leaveAvailable`).
   */
  annual: {
    total: number | null; used: number; pending: number;
    remaining: number | null; available?: number | null;
    /**
     * **넘겨 쓴 만큼**(일) — 안 넘겼으면 0, **부여를 모르면 `null`** (2026-09-11 · A-1).
     * ★ `remaining` 은 0 에서 멈추므로 초과가 그 칸에서는 안 보인다.
     * ★ 옛 서버는 이 칸을 안 준다(`undefined`) — 그때는 **아무 말도 하지 않는다**.
     */
    overdrawn?: number | null;
  };
  substitute: { total: number; used: number; remaining: number };
  monthlyUsage: number[]; // 12 months
  year: number;
  // ── 아래는 2026-08-24 에 더한 칸 — **옛 서버는 안 준다**(전부 optional).
  /** 그 사람의 하루 소정근로(분). 1일이 8시간이 아닌 사람이 있다. */
  dailyMinutes?: number;
  annualMinutes?: {
    total: number | null; used: number; pending: number;
    remaining: number | null; available?: number | null;
    /** 넘겨 쓴 만큼(분) — 위 `annual.overdrawn` 의 분 단위 짝. */
    overdrawn?: number | null;
  };
  /**
   * 대체휴가(보상휴가 §57) 분.
   *
   * ★★ `credited`·`adjusted` 는 **2026-09-06 판 B 에서 더한 칸**이다 —
   *    `credited` 는 휴일대체가 승인돼 **원장에 쌓인** 몫이고, `adjusted` 는 담당자가
   *    연차 관리에서 손으로 넣은 몫이다. 옛 서버는 안 준다(그때는 가르지 않는다).
   */
  substituteMinutes?: {
    total: number; used: number; remaining: number;
    credited?: number; adjusted?: number;
  };
  monthlyUsageMinutes?: number[];
}

export interface TripDto {
  id: string;
  type: string;
  destination: string;
  startDate: string;
  endDate: string;
  purpose: string;
  status: string;
  approvedBy: string | null;
  approvedAt: string | null;
  createdAt: string;
}

export interface OvertimeDto {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  hours: number;
  reason: string;
  status: string;
  approvedBy: string | null;
  approvedAt: string | null;
  createdAt: string;
}

// ─── leaves ─────────────────────────

export async function getLeaveSummary(year?: number): Promise<LeaveSummaryDto> {
  const res = await apiClient.get<LeaveSummaryDto>('/hr/leaves/summary', {
    params: year ? { year } : undefined,
  });
  return res.data;
}

export async function listLeaves(): Promise<LeaveDto[]> {
  const res = await apiClient.get<{ items: LeaveDto[] }>('/hr/leaves');
  return Array.isArray(res.data?.items) ? res.data.items : [];
}

/**
 * 기안자가 고른 결재선 (결재 A6, 2026-08-24) — 근태 신청도 결재선을 보고 고친다.
 *
 * ★ 안 보내면 지금까지처럼 조직에서 자동으로 뽑는다. **옛 서버는 이 칸을 모르고
 *   `additionalProperties:false` 라 400 이 나므로, 호출부는 있을 때만 싣는다.**
 */
export interface ApprovalLineInput {
  approverIds?: string[];
  referrerIds?: string[];
}

export interface CreateLeaveInput extends ApprovalLineInput {
  type: LeaveType;
  startDate: string; // YYYY-MM-DD
  endDate: string;
  reason: string;
  /**
   * 반차 · 반반차(`half` · `quarter`)의 시작 시각 `HH:mm` (S6). 끝 시각은 **보내지 않는다** — 서버가 계산한다.
   * ★ 옛 서버는 이 칸을 모르고 400 이 나므로 새 코드일 때만 싣는다.
   */
  startTime?: string;
}

/**
 * 신청이 결재로 올라간 결과 (근태 5단계, 2026-08-24).
 *
 * **신청이 곧 기안**이다 — 서버가 조직에서 결재선을 뽑아 문서를 만든다. 결재자를 못 찾으면
 * 문서를 만들지 않고(`blocked`) 그 사실을 돌려준다: 빈 결재선 문서는 영원히 진행되지 않아서,
 * 신청을 PENDING 인 채로 두는 편이 정직하다. 옛 서버는 이 칸을 주지 않는다(undefined).
 */
export interface ApprovalSubmitDto {
  documentId: string | null;
  blocked: boolean;
  reason: string | null;
}

export async function createLeave(input: CreateLeaveInput): Promise<LeaveDto> {
  const res = await apiClient.post<{ leave: LeaveDto }>('/hr/leaves', input);
  return res.data.leave;
}

/** 신청 + 결재 결과를 함께 받는다. 옛 서버면 `approval` 이 없다(undefined). */
export async function createLeaveWithApproval(input: CreateLeaveInput): Promise<{ leave: LeaveDto; approval?: ApprovalSubmitDto }> {
  const res = await apiClient.post<{ leave: LeaveDto; approval?: ApprovalSubmitDto }>('/hr/leaves', input);
  return res.data;
}

// ─── business-trips ─────────────────────────

export async function listBusinessTrips(): Promise<TripDto[]> {
  const res = await apiClient.get<{ items: TripDto[] }>('/hr/business-trips');
  return Array.isArray(res.data?.items) ? res.data.items : [];
}

export interface CreateTripInput extends ApprovalLineInput {
  type: TripType;
  destination: string;
  startDate: string;
  endDate: string;
  purpose: string;
}

export async function createBusinessTrip(input: CreateTripInput): Promise<TripDto> {
  const res = await apiClient.post<{ trip: TripDto }>('/hr/business-trips', input);
  return res.data.trip;
}

/** 신청 + 결재 결과를 함께 받는다. */
export async function createBusinessTripWithApproval(input: CreateTripInput): Promise<{ trip: TripDto; approval?: ApprovalSubmitDto }> {
  const res = await apiClient.post<{ trip: TripDto; approval?: ApprovalSubmitDto }>('/hr/business-trips', input);
  return res.data;
}

// ─── overtime ─────────────────────────

export async function listOvertime(): Promise<OvertimeDto[]> {
  const res = await apiClient.get<{ items: OvertimeDto[] }>('/hr/overtime');
  return Array.isArray(res.data?.items) ? res.data.items : [];
}

export interface CreateOvertimeInput {
  date: string;
  startTime: string; // HH:MM
  endTime: string;
  reason: string;
}

export async function createOvertime(input: CreateOvertimeInput): Promise<OvertimeDto> {
  const res = await apiClient.post<{ overtime: OvertimeDto }>('/hr/overtime', input);
  return res.data.overtime;
}

// 순수 계산은 lib 에 있다 — API 모듈을 mock 하는 검사가 여럿이라 여기 두면 mock 마다 걸린다.
export { leaveAvailable } from '../lib/leave-balance';
